import {
	archiveCourse,
	createCourse,
	createDiscount,
	createEnrollment,
	createModule,
	createSection,
	deactivateDiscount,
	deleteModule,
	deleteQuiz,
	deleteSection,
	disableEnrollments,
	enableEnrollment,
	getAdminDashboard,
	getAdminStudent,
	getAdminStudents,
	getCourse,
	getCourses,
	getDiscount,
	getDiscounts,
	reorderModules,
	reorderSections,
	updateCourse,
	updateDiscount,
	updateModule,
	updateSection,
	upsertQuiz,
} from "@/lib/api";
import type {
	AdminStudentsParams,
	CreateCourseRequest,
	CreateDiscountRequest,
	CreateEnrollmentRequest,
	CreateModuleRequest,
	CreateSectionRequest,
	DisableEnrollmentsRequest,
	DisableEnrollmentsResponse,
	EnableEnrollmentRequest,
	EnableEnrollmentResponse,
	UpdateCourseRequest,
	UpdateDiscountRequest,
	UpdateModuleRequest,
	UpdateSectionRequest,
	UpsertQuizRequest,
} from "@/lib/api-types";
import { queryKeys } from "@/lib/query-keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useAdminDashboard() {
	return useQuery({
		queryKey: queryKeys.admin.dashboard,
		queryFn: getAdminDashboard,
	});
}

export function useAdminStudents(params?: AdminStudentsParams) {
	return useQuery({
		queryKey: queryKeys.admin.students,
		queryFn: () => getAdminStudents(params),
	});
}

export function useAdminStudent(id: string) {
	return useQuery({
		queryKey: queryKeys.admin.student(id),
		queryFn: () => getAdminStudent(id),
		enabled: !!id,
	});
}

export function useAdminCourses() {
	return useQuery({
		queryKey: queryKeys.admin.courses,
		queryFn: () => getCourses(),
	});
}

export function useAdminCourse(id: string) {
	return useQuery({
		queryKey: queryKeys.admin.course(id),
		queryFn: () => getCourse(id),
		enabled: !!id && id !== "new",
	});
}

export function useAdminDiscounts() {
	return useQuery({
		queryKey: queryKeys.admin.discounts,
		queryFn: getDiscounts,
	});
}

export function useAdminDiscount(id: string) {
	return useQuery({
		queryKey: queryKeys.admin.discount(id),
		queryFn: () => getDiscount(id),
		enabled: !!id,
	});
}

export function useCreateCourse() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateCourseRequest) => createCourse(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.courses });
		},
	});
}

export function useUpdateCourse(courseId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: UpdateCourseRequest) => updateCourse(courseId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.courses });
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

export function useArchiveCourse() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (courseId: string) => archiveCourse(courseId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.courses });
		},
	});
}

// --- Section mutations ---

export function useCreateSection(courseId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateSectionRequest) => createSection(courseId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

export function useUpdateSection(courseId: string, sectionId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: UpdateSectionRequest) => updateSection(sectionId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

export function useDeleteSection(courseId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (sectionId: string) => deleteSection(sectionId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

export function useReorderSections(courseId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (sectionIds: string[]) => reorderSections(courseId, { section_ids: sectionIds }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

// --- Module mutations ---

export function useCreateModule(courseId: string, sectionId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateModuleRequest) => createModule(sectionId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

export function useUpdateModule(courseId: string, moduleId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: UpdateModuleRequest) => updateModule(moduleId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

export function useDeleteModule(courseId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (moduleId: string) => deleteModule(moduleId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

export function useReorderModules(courseId: string, sectionId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (moduleIds: string[]) => reorderModules(sectionId, { module_ids: moduleIds }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.admin.course(courseId),
			});
		},
	});
}

// --- Quiz mutations ---

export function useUpsertQuiz(moduleId: string) {
	return useMutation({
		mutationFn: (body: UpsertQuizRequest) => upsertQuiz(moduleId, body),
	});
}

export function useDeleteQuiz(moduleId: string) {
	return useMutation({
		mutationFn: () => deleteQuiz(moduleId),
	});
}

// --- Enrollment & Discount mutations ---

export function useCreateEnrollment() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateEnrollmentRequest) => createEnrollment(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.students });
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.dashboard });
		},
	});
}

export function useCreateDiscount() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: CreateDiscountRequest) => createDiscount(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.discounts });
		},
	});
}

export function useUpdateDiscount(discountId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: UpdateDiscountRequest) => updateDiscount(discountId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.discount(discountId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.discounts });
		},
	});
}

export function useDeactivateDiscount(discountId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => deactivateDiscount(discountId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.discount(discountId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.discounts });
		},
	});
}

type EnrollmentAccessRequest =
	| { action: "disable"; body: DisableEnrollmentsRequest }
	| { action: "enable"; body: EnableEnrollmentRequest };

export function useEnrollmentAccess(userId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (
			request: EnrollmentAccessRequest,
		): Promise<DisableEnrollmentsResponse | EnableEnrollmentResponse> =>
			request.action === "enable"
				? enableEnrollment(userId, request.body)
				: disableEnrollments(userId, request.body),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: queryKeys.admin.students }),
				queryClient.invalidateQueries({ queryKey: queryKeys.admin.dashboard }),
				queryClient.invalidateQueries({ queryKey: queryKeys.enrollments }),
				queryClient.invalidateQueries({ queryKey: queryKeys.me }),
			]);
		},
	});
}
